import { useState, useEffect } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, MapPin, Share2, ArrowLeft, Lock, ExternalLink, Video } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { getCategoryLabel } from "@/lib/categories";
import { TicketTierCard } from "@/components/TicketTierCard";
import { SEOHead } from "@/components/SEOHead";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ShareSheet } from "@/components/ShareSheet";
import { BookingSeatMap } from "@/components/booking/BookingSeatMap";

import { EventImage } from "@/components/EventImage";
import { EventAttachmentsDownload } from "@/components/EventAttachmentsDownload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { getLiveRedirectUrl } from "@/lib/urls";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { getEventBySlugWithProducer, manageQueue } from "@/lib/api";
import { getEventProducts } from "@/lib/api-products";
import { getEventPublicPath, getProducerPublicPath, getEventSchedulePath } from "@/lib/urls";
import { getPublishedSchedule } from "@/lib/api-schedule";
import { EventProductCatalog } from "@/components/EventProductCatalog";
import { toast } from "@/hooks/use-toast";
import { cn, formatBRL } from "@/lib/utils";
import { ContactProducerModal } from "@/components/ContactProducerModal";
import { formatEventDateTimeRange, resolveEventTimeZone } from "@/lib/event-timezone";
import { useTranslation } from "react-i18next";
import DOMPurify from "isomorphic-dompurify";
import { ensureAbsoluteUrl } from "@/lib/urls";
import { NOT_FOUND_ILLUSTRATION, resolveEventCoverAbsoluteUrl } from "@/lib/event-cover";
import { ReportEventModal } from "@/components/ReportEventModal";
import { resolveEventFeePercent } from "@/lib/ticket-pricing";

export default function EventDetail() {
  const { t } = useTranslation("eventDetail");
  const { username, slug } = useParams<{ username?: string; slug?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── All hooks must be called before any conditional return ──
  const [unlockCode, setUnlockCode] = useState("");
  const [contactOpen, setContactOpen] = useState(false);
  const [showUnlockInput, setShowUnlockInput] = useState(false);
  const initialTab = new URLSearchParams(window.location.search).get("tab");
  const [activeSection, setActiveSection] = useState<"description" | "tickets" | "venue">(
    initialTab === "tickets" || initialTab === "venue" ? initialTab : "tickets"
  );
  const [revealedTierIds, setRevealedTierIds] = useState<string[]>([]);
  const [showCartCTA, setShowCartCTA] = useState(false);
  const [lastAddedTierName, setLastAddedTierName] = useState("");

  const { setTrackingCode, addItem, itemCount } = useCart();
  const { user } = useAuth();

  const { data: event, isLoading: loadingEvent } = useQuery({
    queryKey: ["event", slug],
    queryFn: () => getEventBySlugWithProducer(slug!),
    enabled: !!slug,
  });

  const { data: eventDetails, isLoading: loadingDetails } = useQuery({
    queryKey: ["event-details", event?.id],
    queryFn: async () => {
      const eventId = event!.id;
      const [tiersResult, productsResult] = await Promise.all([
        supabase
          .from("ticket_tiers")
          .select("*")
          .eq("event_id", eventId)
          .order("sort_order", { ascending: true }),
        getEventProducts(eventId),
      ]);
      if (tiersResult.error) throw tiersResult.error;
      return {
        allTiers: tiersResult.data,
        products: productsResult ?? [],
      };
    },
    enabled: !!event?.id,
  });

  const { data: userTicket } = useQuery({
    queryKey: ["event-user-ticket", event?.id, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tickets")
        .select("id, status")
        .eq("event_id", event!.id)
        .eq("owner_id", user!.id)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!event?.id && !!user?.id,
  });

  useRealtimeSubscription({
    table: "tickets",
    filter: event?.id ? `event_id=eq.${event.id}` : undefined,
    queryKey: ["event-details", event?.id || ""],
    enabled: !!event?.id,
  });

  const { data: hasSchedule } = useQuery({
    queryKey: ["schedule", "avail", event?.id],
    queryFn: async () => {
      const r = await getPublishedSchedule(event!.id);
      return Boolean(r.data?.revision);
    },
    enabled: !!event?.id,
    retry: false,
  });

  // Track promoter click from ?ref=CODE (M03: atomic increment)
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (!ref || !event?.id) return;
    const trackedKey = `promoter_tracked_${event.id}_${ref}`;
    if (sessionStorage.getItem(trackedKey)) return;
    sessionStorage.setItem(trackedKey, "1");
    setTrackingCode(ref);
    supabase
      .from("promoter_events")
      .select("id")
      .eq("tracking_code", ref)
      .eq("event_id", event.id)
      .eq("is_active", true)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          supabase.rpc("increment_promoter_clicks", { p_promoter_event_id: data.id }).then(() => { });
        }
      });
  }, [event?.id, searchParams, setTrackingCode]);

  // M03: Atomic increment views_count once per session per event
  useEffect(() => {
    if (!event?.id) return;
    const viewedKey = `event_viewed_${event.id}`;
    if (sessionStorage.getItem(viewedKey)) return;
    sessionStorage.setItem(viewedKey, "1");
    supabase.rpc("increment_event_views", { p_event_id: event.id }).then(() => { });
  }, [event?.id]);

  // Open contact modal from URL query (?contato=true or ?contact=true)
  useEffect(() => {
    if (searchParams.get("contato") === "true" || searchParams.get("contact") === "true") {
      setContactOpen(true);
    }
  }, [searchParams]);

  // ── Virtual Queue Check ──
  const { data: queueStatus, isLoading: loadingQueue } = useQuery({
    queryKey: ["queue-status", event?.id, user?.id],
    queryFn: () => manageQueue("status", event!.id, user!.id),
    enabled: !!event?.has_virtual_queue && !!event?.id && !!user?.id,
  });

  const shouldGoToQueue = event?.has_virtual_queue && (!user || (queueStatus && queueStatus.status !== "admitted"));

  useEffect(() => {
    if (shouldGoToQueue && !loadingEvent && !loadingQueue) {
      navigate(`/fila/${slug}`, { replace: true });
    }
  }, [shouldGoToQueue, loadingEvent, loadingQueue, navigate, slug]);

  // ── Canonical redirect (legacy URLs without /produtor/:username) ──
  const producer = (event as any)?.profiles || null;
  const organization = (event as any)?.organizations || null;
  const isOrg = !!event?.organization_id && !!organization?.slug;
  const canonicalProducerSlug = isOrg ? organization.slug : producer?.organizer_slug;
  const canonicalPath = slug && canonicalProducerSlug ? getEventPublicPath(slug, canonicalProducerSlug, isOrg) : null;

  const entityName = isOrg ? organization.name : (producer?.full_name || "Produtor");
  const entityLogo = isOrg ? organization.logo_url : (producer?.organizer_logo_url || producer?.avatar_url);
  const entityBio = isOrg ? organization.description : producer?.organizer_bio;

  const shouldRedirect = !loadingEvent && event && canonicalProducerSlug && (
    username !== canonicalProducerSlug || !username
  ) && !shouldGoToQueue; // Avoid double redirect

  useEffect(() => {
    if (shouldRedirect && canonicalPath) {
      // window.location.replace em vez de navigate() — corrige bug do Safari onde
      // history.replaceState atualiza a URL bar mas o clipboard ainda copia a URL anterior
      window.location.replace(canonicalPath);
    }
  }, [shouldRedirect, canonicalPath]);

  // ── Conditional returns (after ALL hooks) ──
  if (shouldRedirect || shouldGoToQueue || (event?.has_virtual_queue && loadingQueue)) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (loadingEvent) {
    return (
      <>
        <div><Skeleton className="w-full h-[340px]" />
          <div className="container py-8 space-y-4"><Skeleton className="h-10 w-2/3" /><Skeleton className="h-5 w-1/3" /></div>
        </div>
      </>
    );
  }

  if (!event) {
    return (
      <>
        <div className="min-h-[70vh] bg-white">
          <div className="container pt-24 pb-16 text-center max-w-lg">
            <img
              src={NOT_FOUND_ILLUSTRATION}
              alt=""
              width={768}
              height={512}
              className="mx-auto mb-6 w-full max-w-sm bg-white"
            />
            <h1 className="font-display text-2xl font-bold mb-2 text-neutral-900">{t("eventNotFound")}</h1>
            <p className="text-neutral-500 mb-6">{t("eventNotFoundDesc")}</p>
            <Button asChild><Link to="/eventos">{t("viewAllEvents")}</Link></Button>
          </div>
        </div>
      </>
    );
  }

  // ── Derived state & handlers ──
  const allTiers = eventDetails?.allTiers;
  const products = eventDetails?.products ?? [];
  const loadingTiers = loadingDetails;

  const resolvedSeatMapConfig = event?.has_seat_map ? {
    imageUrl: (event.seat_map_config as any)?.imageUrl,
    tierColors: (() => {
      const colors: Record<string, string> = { ...((event.seat_map_config as any)?.tierColors || {}) };
      const SECTOR_COLORS = [
        "#E53E3E", "#DD6B20", "#D69E2E", "#38A169", "#3182CE",
        "#805AD5", "#D53F8C", "#2B6CB0", "#2C7A7B", "#9B2C2C",
      ];
      (allTiers || []).forEach((t: any, idx: number) => {
        if (!colors[t.name]) {
          colors[t.name] = t.sector_color || SECTOR_COLORS[idx % SECTOR_COLORS.length];
        }
      });
      return colors;
    })()
  } : null;

  const tiers = allTiers?.filter((t: any) => {
    if (!t.is_visible || t.name === "Lista de Convidado") return false;
    if (t.is_hidden_by_default) {
      return revealedTierIds.includes(t.id);
    }
    return true;
  }) || [];

  const hasHiddenTiers = allTiers?.some((t: any) => t.is_hidden_by_default && t.is_visible) || false;

  const handleUnlock = async () => {
    const code = unlockCode.trim();
    if (!code || !event?.id) return;
    const { data: tierIds, error } = await supabase.rpc("validate_unlock_code", {
      p_event_id: event.id,
      p_code: code,
    });
    if (error || !tierIds || (tierIds as string[]).length === 0) {
      toast({ title: t("toast.invalidCodeTitle"), description: t("toast.invalidCodeDesc"), variant: "destructive" });
    } else {
      setRevealedTierIds((prev) => [...prev, ...(tierIds as string[])]);
      setUnlockCode("");
      setShowUnlockInput(false);
      toast({ title: t("toast.validCodeTitle"), description: t("toast.validCodeDesc") });
    }
  };

  const handleAddToCart = (tierId: string, quantity: number) => {
    const tier = allTiers?.find((t: any) => t.id === tierId);
    if (!tier || !event) return;
    addItem({
      tierId: tier.id, eventId: event.id, tierName: tier.name,
      eventTitle: event.title, eventSlug: event.slug,
      producerUsername: canonicalProducerSlug,
      price: tier.price ?? 0, quantity,
      coverImageUrl: event.cover_image_url ?? undefined,
      platformFeePercent: resolveEventFeePercent(event.platform_fee_percent),
      maxPerOrder: tier.max_per_order ?? 10,
      absorbFee: tier.absorb_fee ?? false,
    });
    setLastAddedTierName(tier.name);
    setShowCartCTA(true);
  };

  const handleQuickCheckout = () => {
    if (!event || tiers.length === 0) {
      setActiveSection("tickets");
      return;
    }
    const checkoutTier = tiers.find((tier: any) => (tier.price ?? 0) === 0) ?? tiers[0];
    if (!checkoutTier) {
      setActiveSection("tickets");
      return;
    }
    addItem({
      tierId: checkoutTier.id,
      eventId: event.id,
      tierName: checkoutTier.name,
      eventTitle: event.title,
      eventSlug: event.slug,
      producerUsername: canonicalProducerSlug,
      price: checkoutTier.price ?? 0,
      quantity: 1,
      coverImageUrl: event.cover_image_url ?? undefined,
      platformFeePercent: resolveEventFeePercent(event.platform_fee_percent),
      maxPerOrder: checkoutTier.max_per_order ?? 10,
      absorbFee: checkoutTier.absorb_fee ?? false,
    });
    setLastAddedTierName(checkoutTier.name);
    setShowCartCTA(true);
    navigate("/checkout");
  };


  const eventTimeZone = resolveEventTimeZone((event as any)?.time_zone);

  const startDate = new Date(event.start_date);
  const endDate = new Date(event.end_date);
  const sections = [
    { id: "description" as const, label: t("tabs.about") },
    { id: "tickets" as const, label: t("tabs.tickets") },
    { id: "venue" as const, label: t("tabs.venue") },
  ];

  const lowestPrice = tiers.length > 0 ? Math.min(...tiers.map((t: any) => t.price ?? 0)) : null;

  return (
    <div className="pb-[144px] lg:pb-0">
      <SEOHead
        title={event.title}
        description={event.description?.slice(0, 155) || `Compre ingressos para ${event.title} no TicketHall`}
        ogImage={resolveEventCoverAbsoluteUrl(event.cover_image_url)}
        ogType="event"
        canonicalUrl={canonicalPath ? `${window.location.origin}${canonicalPath}` : `${window.location.origin}/eventos/${event.slug}`}
        jsonLd={{
          "@context": "https://schema.org", "@type": "Event",
          name: event.title, startDate: event.start_date, endDate: event.end_date,
          location: event.is_online
            ? { "@type": "VirtualLocation", url: event.streaming_redirect_token ? getLiveRedirectUrl(event.streaming_redirect_token) : ensureAbsoluteUrl(event.online_url) }
            : { "@type": "Place", name: event.venue_name, address: { "@type": "PostalAddress", addressLocality: event.venue_city, addressRegion: event.venue_state } },
          image: resolveEventCoverAbsoluteUrl(event.cover_image_url), description: event.description,
          organizer: { "@type": "Organization", name: "TicketHall" },
        }}
      />

      {/* Breadcrumb */}
      {(producer || organization) && canonicalProducerSlug && (
        <div className="container mt-6 text-sm text-muted-foreground">
          <nav className="flex flex-wrap items-center gap-2">
            <Link to="/" className="hover:text-foreground transition-colors">TicketHall</Link>
            <span>›</span>
            <Link to={getProducerPublicPath(canonicalProducerSlug, isOrg)} className="hover:text-foreground transition-colors">@{canonicalProducerSlug}</Link>
            <span>›</span>
            <span className="text-foreground">{event.title}</span>
          </nav>
        </div>
      )}
      {/* Cover image - full banner */}
      <div className="relative w-full flex justify-center bg-secondary overflow-hidden">
        <EventImage src={event.cover_image_url} alt={event.title} className="w-auto max-w-full max-h-[380px] object-contain" />
        <FavoriteButton eventId={event.id} size="md" className="absolute top-4 right-4" />
      </div>

      <div className="container relative pb-16 mt-6">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main content */}
          <div className="flex-1 space-y-6">
            {/* Back + category badge */}
            <div>
              <Link to={canonicalProducerSlug ? getProducerPublicPath(canonicalProducerSlug, isOrg) : "/eventos"} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3">
                <ArrowLeft className="h-4 w-4" /> {t("back")}
              </Link>
              {event.category && (
                <span className="ml-3 inline-block px-2.5 py-1 text-xs font-medium rounded-full bg-primary/90 text-primary-foreground">
                  {getCategoryLabel(event.category)}
                </span>
              )}
            </div>

            <h1 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-foreground leading-tight">{event.title}</h1>

            {/* Meta row with icons */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-5 gap-y-3 text-sm">
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <Calendar className="h-4 w-4 text-primary" />
                {formatEventDateTimeRange(startDate, endDate, eventTimeZone, { dateStyle: "long" })}
              </span>
              {event.venue_name && (
                <span className="inline-flex items-start gap-1.5 text-muted-foreground">
                  <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    {event.venue_name}
                    {event.venue_address ? ` - ${event.venue_address}` : ""}
                    {event.venue_city ? `, ${event.venue_city}` : ""}
                    {event.venue_state ? `, ${event.venue_state}` : ""}
                    {event.venue_zip ? ` - ${event.venue_zip}` : ""}
                  </span>
                </span>
              )}
              {/* max_capacity removed from schema */}
            </div>

            {/* Section tabs - underline style */}
            <div className="flex border-b border-border gap-6 overflow-x-auto no-scrollbar" role="tablist">
              {sections.map((s) => (
                <button
                  key={s.id}
                  role="tab" aria-selected={activeSection === s.id} onClick={() => setActiveSection(s.id)}
                  className={cn(
                    "pb-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap",
                    activeSection === s.id
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {hasSchedule && slug ? (
              <Link
                to={getEventSchedulePath(slug, canonicalProducerSlug ?? undefined, isOrg)}
                className="block rounded-[22px] border border-border/60 bg-card p-4 text-sm font-medium"
              >
                Ver programação
              </Link>
            ) : null}

            {/* Description section */}
            {activeSection === "description" && (
              <div className="space-y-6">
                <div
                  className="th-rich-text text-sm text-muted-foreground leading-relaxed prose dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: event.description
                      ? DOMPurify.sanitize(
                        event.description.includes('<') && event.description.includes('>')
                          ? event.description
                          : event.description.replace(/\n/g, '<br />'),
                        { ALLOWED_TAGS: ['p', 'strong', 'em', 'h2', 'h3', 'ul', 'ol', 'li', 'a', 'img', 'br'], ALLOWED_ATTR: ['href', 'src', 'target', 'rel', 'class', 'alt', 'title'] }
                      )
                      : t("noDescription")
                  }}
                />
                {/* minimum_age removed from schema */}

                {/* Materiais para download (anexos públicos) */}
                <EventAttachmentsDownload eventId={event.id} />

                {/* Produzido por */}
                {(producer || organization) && (
                  <div className="border-t border-border pt-6 space-y-3">
                    <h3 className="font-display font-semibold text-foreground">{isOrg ? t("producedByOrg") : t("producedBy")}</h3>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12">
                        {entityLogo ? (
                          <AvatarImage src={entityLogo} alt={entityName} />
                        ) : null}
                        <AvatarFallback className="bg-primary/10 text-primary font-bold">
                          {entityName.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold text-foreground">{entityName}</p>
                        {canonicalProducerSlug && (
                          <Link
                            to={getProducerPublicPath(canonicalProducerSlug, isOrg)}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-0.5"
                          >
                            {isOrg ? t("moreEventsFromOrg") : t("moreEventsFromProducer")} <ExternalLink className="h-3 w-3" />
                          </Link>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={() => setContactOpen(true)}
                        >
                          {isOrg ? t("contactOrg") : t("contactProducer")}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tickets section */}
            {activeSection === "tickets" && (
              <div className="space-y-3">
                {resolvedSeatMapConfig?.imageUrl && (
                  <div className="mb-6 p-4 rounded-xl border border-border bg-card/50 backdrop-blur-sm space-y-3 animate-in fade-in duration-200">
                    <h3 className="font-display font-semibold text-foreground flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" /> {t("sectorMap")}
                    </h3>
                    <div className="max-w-lg mx-auto">
                      <BookingSeatMap seatMapConfig={resolvedSeatMapConfig} tiers={allTiers || []} />
                    </div>
                  </div>
                )}

                {loadingTiers ? (
                  Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)
                ) : tiers.length > 0 ? (
                  (() => {
                    const eligibleTierIds = (event?.certificate_config as any)?.eligible_tier_ids;
                    const hasRestriction = Array.isArray(eligibleTierIds) && eligibleTierIds.length > 0;
                    return tiers.map((tier: any) => (
                      <TicketTierCard key={tier.id} id={tier.id} name={tier.name} description={tier.description}
                        price={tier.price ?? 0} originalPrice={tier.original_price}
                        platformFeePercent={event.platform_fee_percent}
                        absorbFee={tier.absorb_fee}
                        quantityTotal={tier.quantity_total} quantitySold={tier.quantity_sold ?? 0}
                        quantityReserved={tier.quantity_reserved ?? 0}
                        minPerOrder={tier.min_per_order ?? 1} maxPerOrder={tier.max_per_order ?? 10}
                        tierType={tier.tier_type ?? "paid"} onAdd={handleAddToCart}
                        salesStartDate={tier.sale_start_date}
                        salesEndDate={tier.sale_end_date}
                        timeZone={eventTimeZone}
                        eventId={event.id}
                        sectorColor={resolvedSeatMapConfig?.tierColors?.[tier.name]}
                        includesCertificate={!hasRestriction || eligibleTierIds.includes(tier.id)} />
                    ));
                  })()
                ) : (
                  <p className="text-muted-foreground text-sm py-4">{t("noTicketsAvailable")}</p>
                )}

                {hasHiddenTiers && (
                  <div className="pt-2">
                    {!showUnlockInput ? (
                      <button onClick={() => setShowUnlockInput(true)} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
                        <Lock className="h-3.5 w-3.5" /> {t("accessCodePrompt")}
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <Input value={unlockCode} onChange={(e) => setUnlockCode(e.target.value)} placeholder={t("accessCodePlaceholder")} className="max-w-xs bg-secondary border-border-strong" maxLength={50}
                          onKeyDown={(e) => e.key === "Enter" && handleUnlock()} />
                        <Button size="sm" onClick={handleUnlock}>{t("unlock")}</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setShowUnlockInput(false); setUnlockCode(""); }}>{t("cancel")}</Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Products Catalog */}
                {products.length > 0 && (
                  <div className="pt-6">
                    <EventProductCatalog eventId={event.id} />
                  </div>
                )}
              </div>
            )}

            {/* Venue section */}
            {activeSection === "venue" && (
              <div>
                {event.venue_name ? (
                  <div className="space-y-2">
                    <p className="font-semibold text-foreground">{event.venue_name}</p>
                    {event.venue_address && <p className="text-sm text-muted-foreground">{event.venue_address}</p>}
                    <p className="text-sm text-muted-foreground">
                      {[event.venue_city, event.venue_state].filter(Boolean).join(", ")}
                      {event.venue_zip ? ` — CEP ${event.venue_zip}` : ""}
                    </p>
                    {/* Google Maps embed */}
                    {event.venue_latitude && event.venue_longitude ? (
                      <div className="w-full h-64 rounded-lg overflow-hidden border border-border mt-4">
                        <iframe
                          title={t("mapTitle")}
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          src={`https://maps.google.com/maps?q=${event.venue_latitude},${event.venue_longitude}&z=15&output=embed`}
                        />
                      </div>
                    ) : event.venue_address ? (
                      <div className="w-full h-64 rounded-lg overflow-hidden border border-border mt-4">
                        <iframe
                          title={t("mapTitle")}
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          src={`https://maps.google.com/maps?q=${encodeURIComponent([event.venue_name, event.venue_address, event.venue_city].filter(Boolean).join(", "))}&output=embed`}
                        />
                      </div>
                    ) : (
                      <div className="w-full h-48 bg-secondary rounded-lg flex items-center justify-center text-sm text-muted-foreground border border-border mt-4">
                        {t("mapUnavailable")}
                      </div>
                    )}
                    {event.venue_name && (
                      <Link
                        to={`/eventos?q=${encodeURIComponent(event.venue_name)}`}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                      >
                        {t("moreEventsAtVenue")} <ExternalLink className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                ) : event.is_online ? (
                  <p className="text-sm text-muted-foreground">{t("onlineEvent")}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">{t("venueTba")}</p>
                )}
              </div>
            )}

            {/* Producer Profile Section */}
            {(producer || organization) && (
              <div
                className="mt-12 rounded-xl border border-border bg-card p-5 sm:p-6 flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-5 text-center sm:text-left transition-colors hover:bg-muted/50 cursor-pointer group"
                onClick={() => navigate(getProducerPublicPath(canonicalProducerSlug, isOrg))}
              >
                <div className="h-16 w-16 sm:h-20 sm:w-20 shrink-0 rounded-full overflow-hidden bg-muted border-2 border-background transition-transform group-hover:scale-105">
                  {entityLogo ? (
                    <img
                      src={entityLogo}
                      alt={entityName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center bg-primary/10 text-primary text-xl font-bold font-display">
                      {entityName[0].toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg font-bold text-foreground">
                    {entityName}
                  </h3>
                  {entityBio ? (
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                      {entityBio}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-1">
                      {isOrg ? t("producerSection.orgRole") : t("producerSection.producerRole")}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-4 justify-center sm:justify-start">
                    <Button variant="default" size="sm" asChild>
                      <Link to={getProducerPublicPath(canonicalProducerSlug, isOrg)} onClick={(e) => e.stopPropagation()}>
                        {t("producerSection.viewFullProfile")}
                      </Link>
                    </Button>
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setContactOpen(true); }}>
                      {isOrg ? t("producerSection.contactOrg") : t("producerSection.contactProducer")}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar sticky CTA */}
          <aside className="hidden lg:block w-80 shrink-0">
            <div className="sticky top-20 space-y-4 p-5 rounded-xl border border-border bg-card">
              {event.is_online && userTicket ? (
                <>
                  <h3 className="font-display font-semibold text-foreground">{t("sidebar.stream")}</h3>
                  <p className="text-sm text-muted-foreground">{t("sidebar.hasOnlineTicket")}</p>
                  <Button className="w-full gap-2" asChild>
                    <a
                      href={event.streaming_redirect_token ? getLiveRedirectUrl(event.streaming_redirect_token) : ensureAbsoluteUrl(event.online_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Video className="h-4 w-4" /> {t("sidebar.watchLive")}
                    </a>
                  </Button>
                </>
              ) : (
                <>
                  <h3 className="font-display font-semibold text-foreground">{t("tabs.tickets")}</h3>
                  {lowestPrice !== null ? (
                    <>
                      <p className="text-sm text-muted-foreground">
                        {t("startingFrom")}{" "}
                        <span className="text-foreground font-semibold">
                          {lowestPrice === 0 ? t("free") : formatBRL(lowestPrice)}
                        </span>
                      </p>
                      <Button className="w-full" onClick={() => lowestPrice === 0 ? handleQuickCheckout() : setActiveSection("tickets")}>
                        {lowestPrice === 0 ? t("register") : t("viewTickets")}
                      </Button>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t("comingSoon")}</p>
                  )}
                </>
              )}
              <ShareSheet url={window.location.href} title={event.title}>
                <Button variant="outline" size="sm" className="w-full gap-2">
                  <Share2 className="h-4 w-4" /> {t("share")}
                </Button>
              </ShareSheet>
            </div>
          </aside>
        </div>

        <ReportEventModal eventId={event.id} />
      </div>

      {/* Cart CTA bar — shown after adding to cart */}
      {showCartCTA && (
        <div className={`fixed ${user ? "bottom-[72px]" : "bottom-0"} lg:bottom-0 left-0 right-0 z-40 bg-primary text-primary-foreground p-4 animate-fade-in`}>
          <div className="container flex items-center justify-between gap-4 max-w-lg mx-auto">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">✓ {lastAddedTierName} {t("addedToCart")}</p>
              <p className="text-xs opacity-80">{itemCount} {itemCount === 1 ? t("item") : t("items")} {t("inCart")}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => navigate("/carrinho")} className="text-xs bg-primary-foreground text-primary hover:bg-primary-foreground/90">
                {t("finishCheckout")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile sticky CTA - above bottom nav */}
      {!showCartCTA && activeSection !== "tickets" && (
        <div className={`lg:hidden fixed ${user ? "bottom-[72px]" : "bottom-0"} left-0 right-0 z-40 bg-card/95 backdrop-blur-lg border-t border-border p-4 pb-safe`}>
          <div className="flex items-center justify-between gap-4">
            {event.is_online && userTicket ? (
              <Button className="flex-1 gap-2" asChild>
                <a
                  href={event.streaming_redirect_token ? getLiveRedirectUrl(event.streaming_redirect_token) : ensureAbsoluteUrl(event.online_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Video className="h-4 w-4" /> {t("sidebar.watchLive")}
                </a>
              </Button>
            ) : (
              <>
                <div>
                  <p className="text-xs text-muted-foreground">{t("startingFrom")}</p>
                  <p className="font-display font-bold text-foreground">
                    {lowestPrice !== null ? (lowestPrice === 0 ? t("free") : formatBRL(lowestPrice)) : "—"}
                  </p>
                </div>
                <Button className="flex-1 max-w-[200px]" onClick={() => lowestPrice === 0 ? handleQuickCheckout() : setActiveSection("tickets")}>
                  {lowestPrice === 0 ? t("register") : t("viewTickets")}
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Contact Producer Modal */}
      {producer && (
        <ContactProducerModal
          open={contactOpen}
          onOpenChange={setContactOpen}
          producerId={producer.id}
          producerName={entityName}
          eventId={event?.id}
          eventTitle={event?.title}
          initialSubject={searchParams.get("assunto") || searchParams.get("subject") || undefined}
          initialDirectToForm={searchParams.get("contato") === "true" || searchParams.get("contact") === "true"}
        />
      )}
    </div>
  );
}
